/**
 * @file `/export` route. Parses URL query/fragment, scrubs the transport_key
 * from history, then delegates state machine + UI to `useExportSession`.
 */

import { useEffect, useMemo, useState } from "react";

import { AwaitingApproval } from "@/features/export/components/AwaitingApproval";
import { ExportComplete } from "@/features/export/components/ExportComplete";
import { ExportError } from "@/features/export/components/ExportError";
import { ExportProgressView } from "@/features/export/components/ExportProgressView";
import { useExportSession } from "@/features/export/hooks/useExportSession";
import {
  InvalidExportUrlError,
  parseExportLocation,
  scrubTransportKeyFromHistory,
} from "@/features/export/lib/url";
import { getFirebaseFirestore, getFirebaseStorage } from "@/shared/firebase/client";

interface UrlParams {
  sessionId: string;
  transportKeyB64Url: string;
}

export function ExportRoute() {
  const [parseError, setParseError] = useState<string | null>(null);
  const [params, setParams] = useState<UrlParams | null>(null);

  // Parse + scrub the URL once on mount, before any render that would expose
  // the fragment back to the user via the address bar.
  useEffect(() => {
    try {
      const { sessionId, transportKeyB64Url } = parseExportLocation({
        search: window.location.search,
        hash: window.location.hash,
      });
      scrubTransportKeyFromHistory();
      setParams({ sessionId, transportKeyB64Url });
    } catch (e) {
      if (e instanceof InvalidExportUrlError) {
        setParseError(e.message);
      } else {
        setParseError(String(e));
      }
    }
  }, []);

  if (parseError) {
    return <ExportError phase="failed" category="session_invalid" />;
  }
  if (!params) {
    return <Loading />;
  }
  return <ExportSessionInner {...params} />;
}

function ExportSessionInner({ sessionId, transportKeyB64Url }: UrlParams) {
  const firestore = useMemo(() => getFirebaseFirestore(), []);
  const storage = useMemo(() => getFirebaseStorage(), []);

  const state = useExportSession({
    firestore,
    storage,
    sessionId,
    transportKeyB64Url,
  });

  // Warn the user if they try to close the tab while exporting.
  useEffect(() => {
    if (state.phase !== "exporting") return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [state.phase]);

  switch (state.phase) {
    case "loading":
    case "approved":
    case "consuming":
      return <Loading />;
    case "awaiting_approval":
      return state.session ? <AwaitingApproval session={state.session} /> : <Loading />;
    case "consumed" as typeof state.phase:
      return <ConsumedReady onStart={() => void state.startExport()} />;
    case "exporting":
      return state.progress ? <ExportProgressView progress={state.progress} /> : <Loading />;
    case "completed":
      return <ExportComplete progress={state.progress} />;
    case "failed":
    case "rejected":
    case "expired":
      return <ExportError phase={state.phase} category={state.error?.category ?? null} />;
  }
}

function Loading() {
  return (
    <section>
      <h1 style={{ marginTop: 0 }}>準備しています…</h1>
      <p style={{ color: "var(--color-text-sub)" }}>
        モバイル側の状態を確認しています。少々お待ちください。
      </p>
    </section>
  );
}

function ConsumedReady({ onStart }: { onStart: () => void }) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <h1 style={{ marginTop: 0 }}>承認されました</h1>
      <p style={{ color: "var(--color-text-sub)", lineHeight: 1.7 }}>
        保存先を指定してエクスポートを開始してください。エクスポート中はこのタブを
        閉じないでください。
      </p>
      <button
        type="button"
        onClick={onStart}
        style={{
          alignSelf: "flex-start",
          padding: "12px 24px",
          borderRadius: "12px",
          border: "1px solid var(--color-border)",
          background: "var(--color-accent, #4a8)",
          color: "#fff",
          fontSize: "14px",
          cursor: "pointer",
        }}
      >
        保存先を選んでエクスポート
      </button>
    </section>
  );
}
